import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Channel, Chat, ChatContextValue, Thread, useChatContext, Window } from "stream-chat-react";
import "stream-chat-react/dist/css/index.css";
import * as StreamChat from "stream-chat";
import classnames from "classnames";

import { getAllowGeneralChatInput, getCreatedGeneralChannel } from "../../store/selectors";
import { MESSAGE_UPDATE_EVENTS } from "../../constants";
import { authentificated, getMember } from "../../../Auth/store/selectors";
import { CustomDateSeparator, CustomMessageInputWrapper, CustomMessageList, EmptyChatState } from "../../components";
import { EditMessageObj, IChannelTypes } from "../../interfaces";
import { useGetStream } from "../../../../shared/hooks/GetStreamHook";

import { getAllowEnded, getEvent, getEventSettings } from "containers/Sermons/store/selectors";
import { EventAuthAlert } from "shared/components/Event/EventAuthAlert";
import { DiscussionTab } from "shared/components/Event/EventSideDialog/EventSideDialogHeader/EventSideDialogDiscussionTabs";
import { EventDashboardTabs, EventStatus, SermonRoles } from "containers/Sermons/interfaces";
import { createGeneralChat, setAllowGeneralInput, setUnreadMessagesCount } from "containers/Discussions/store/actions";
import { AlertDialog, CanView, usePermissions } from "shared";
import { EventPermissions } from "containers/Auth";
import { checkMeetingRole, getEventTypeName } from "containers/Sermons";
import { getChannelUnreadMessagesCount } from "shared/utils/getStream";

const emptyEditMessage = {};

interface ChatContextProxyProps {
  onContextLoaded: (context: ChatContextValue) => void;
}

interface Props {
  openRestrictionAlert: () => void;
}

const ChatContextProxy: React.FC<ChatContextProxyProps> = ({ onContextLoaded }) => {
  const chatContext = useChatContext();

  useEffect(() => {
    onContextLoaded(chatContext);
  }, [onContextLoaded, chatContext]);
  return <></>;
};

const GeneralDiscussionContainer: React.FC<Props> = ({ openRestrictionAlert }) => {
  const chatClient = useGetStream();

  const isAuthenticated = useSelector(authentificated());
  const event = useSelector(getEvent());
  const allowGeneralChatInput = useSelector(getAllowGeneralChatInput());
  const { selectedTab, activeDashboardTab } = useSelector(getEventSettings());
  const allowEnded = useSelector(getAllowEnded());
  const currentUserMember = useSelector(getMember());
  const createdGeneralChatId = useSelector(getCreatedGeneralChannel());

  const [generalChannel, setGeneralChannel] = useState<StreamChat.Channel>();
  const [editMessageDetails, setEditMessageDetails] = useState<EditMessageObj>(emptyEditMessage);
  const [authAlertOpened, setAuthAlertOpened] = useState(false);
  const [alertDialogOpened, setAlertDialogOpened] = useState(false);
  const [chatContext, setChatContext] = useState<ChatContextValue>();

  const dispatch = useDispatch();
  const canEdit = usePermissions([EventPermissions.edit], event?.community_id);

  const showMessageList = selectedTab === DiscussionTab.general && activeDashboardTab === EventDashboardTabs.chat;

  const eventTypeName = useMemo(() => getEventTypeName(event?.type), [event]);

  const allowGeneralChatInputUser = useMemo(
    () =>
      !!event &&
      event.status !== EventStatus.ended &&
      (canEdit ||
        (!!currentUserMember &&
          checkMeetingRole(event, currentUserMember, [SermonRoles.greeterHost, SermonRoles.greeter]))),
    [canEdit, currentUserMember, event],
  );

  const getTabState = useCallback(
    () => selectedTab === DiscussionTab.general && activeDashboardTab === EventDashboardTabs.chat,
    [selectedTab, activeDashboardTab],
  );
  const channelEventHandler = useCallback(
    event => {
      if (MESSAGE_UPDATE_EVENTS.includes(event.type)) {
        if (!getTabState()) {
          const eventGeneralUnread = getChannelUnreadMessagesCount(generalChannel);
          dispatch(setUnreadMessagesCount({ eventGeneralUnread }));
        }
      }
    },
    [dispatch, generalChannel, getTabState],
  );
  const onContextLoaded = useCallback(
    (context: ChatContextValue) => {
      if (!chatContext) {
        setChatContext(context);
      }
    },
    [chatContext],
  );

  useEffect(() => {
    if (generalChannel) {
      generalChannel.on(channelEventHandler);
    }
    return () => {
      generalChannel && generalChannel.off(channelEventHandler);
    };
  }, [generalChannel, dispatch, getTabState, channelEventHandler]);

  useEffect(() => {
    if (generalChannel && getTabState()) {
      generalChannel.markRead();
      dispatch(setUnreadMessagesCount({ eventGeneralUnread: 0 }));
    }
  }, [generalChannel, chatClient, dispatch, getTabState]);

  useEffect(() => {
    if (
      event &&
      (event.was_started || !event.restricted_generalChat) &&
      (event.status !== EventStatus.ended || allowEnded) &&
      !allowGeneralChatInput
    ) {
      dispatch(setAllowGeneralInput(true));
    }
  }, [dispatch, event, allowEnded, allowGeneralChatInput]);

  useEffect(() => {
    return () => {
      dispatch(setAllowGeneralInput(false));
    };
  }, [dispatch]);

  const handleStopEditing = useCallback(() => {
    setEditMessageDetails(emptyEditMessage);
  }, []);

  const getGeneralChannel = useCallback(async () => {
    if (!event || !chatContext) {
      return;
    }
    const channels = await chatContext.client?.queryChannels(
      {
        type: IChannelTypes.livestream,
        event_id: event.id,
      },
      {},
      {},
    );
    const channel = channels?.[0];
    if (!channel) {
      dispatch(createGeneralChat.request({ meeting_id: event.id }));
      return;
    }
    setGeneralChannel(channel);
  }, [chatContext, event, dispatch]);

  const reloadChannel = useCallback(() => {
    setGeneralChannel(undefined);
    getGeneralChannel();
  }, [getGeneralChannel]);

  const handleClickInput = () => {
    if (!isAuthenticated) {
      setAuthAlertOpened(true);
    } else if (!allowGeneralChatInput && !allowGeneralChatInputUser && !allowEnded) {
      setAlertDialogOpened(true);
    }
  };

  useEffect(() => {
    if (createdGeneralChatId) {
      reloadChannel();
    }
  }, [createdGeneralChatId, reloadChannel, dispatch]);

  useEffect(
    () => () => {
      dispatch(createGeneralChat.success(null));
    },
    [dispatch],
  );

  useEffect(() => {
    if (!chatContext) {
      return;
    }
    getGeneralChannel();
  }, [getGeneralChannel, chatContext]);

  useEffect(() => {
    if (!chatContext || !generalChannel) {
      return;
    }
    chatContext.setActiveChannel && chatContext.setActiveChannel(generalChannel);
  }, [chatContext, generalChannel]);

  const channelClasses = classnames("channel", "channel-event", "channel-fullWidth");

  if (!chatClient) {
    return null;
  }

  return (
    <>
      <EventAuthAlert
        title="Chat"
        message="You need to be logged in to chat with other people. Please sign up or log in."
        open={authAlertOpened}
        onClose={() => setAuthAlertOpened(false)}
      />
      <AlertDialog
        mode="info"
        title="Host has not yet enabled General Chat"
        message={`Sorry the Host has not enabled the General Chat for this ${eventTypeName} yet. The ${eventTypeName} chat can be enabled in Event settings by an Admin.`}
        open={alertDialogOpened}
        onConfirm={() => setAlertDialogOpened(false)}
        confirmText="Got It"
        variant="brown"
        dialogClassName="alert-dialog-popup"
      />
      <Chat client={chatClient}>
        <ChatContextProxy onContextLoaded={onContextLoaded} />
        <div className={classnames("channelWrap", "channelWrap-event", { anonymous: !isAuthenticated }, "stream")}>
          <div className={channelClasses}>
            {showMessageList && (
              <Channel
                DateSeparator={CustomDateSeparator}
                EmptyStateIndicator={() => <EmptyChatState listType="message" theme={"stream"} />}
              >
                <Window>
                  <CanView condition={showMessageList}>
                    <CustomMessageList
                      theme="stream"
                      generalChannel
                      setEditMessageDetails={setEditMessageDetails}
                      reloadChannel={reloadChannel}
                    />
                  </CanView>
                  {event &&
                    ([EventStatus.live, EventStatus.lobby, EventStatus.scheduled].includes(event.status) ||
                      (EventStatus.ended === event?.status && allowEnded)) && (
                      <CustomMessageInputWrapper
                        handleStopEditing={handleStopEditing}
                        editMessageDetails={editMessageDetails}
                        theme="stream"
                        disabledInput={
                          !isAuthenticated || (!allowGeneralChatInput && !allowGeneralChatInputUser && !allowEnded)
                        }
                        handleClickInput={handleClickInput}
                        uniqueTooltipId={"general-discussions-toolotip-id"}
                        openRestrictionAlert={openRestrictionAlert}
                      />
                    )}
                </Window>
                <Thread />
              </Channel>
            )}
          </div>
        </div>
      </Chat>
    </>
  );
};

export default GeneralDiscussionContainer;
